import { useForm } from 'react-hook-form';

import { Or2Dialog } from '@/components/feedback/Or2Dialog';
import { graphql, type DocumentType } from '@/gql';

import { Or2Button } from '@/components/button/Or2Button';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION } from '../AssistantAiResponseFeedbackLikeButton';

import type { AssistantAiResponseFeedbackFormData } from './feedback-dialog/AssistantAiResponseFeedbackForm';
import { AssistantAiResponseFeedbackForm } from './feedback-dialog/AssistantAiResponseFeedbackForm';

const ASSISTANT_AI_RESPONSE_FEEDBACK_COMMENT_DIALOG_ITEM_FRAGMENT = graphql(`
  fragment assistantAiResponseFeedbackCommentDialogItemFields on OrtooGptConversationItem {
    _id
  }
`);

const ASSISTANT_AI_RESPONSE_FEEDBACK_COMMENT_DIALOG_FEEDBACK_FRAGMENT = graphql(
  `
    fragment assistantAiResponseFeedbackCommentDialogFeedbackFields on OrtooGptConversationItemFeedback {
      user {
        _id
      }
      rating
      comment
    }
  `,
);

interface Props {
  item: DocumentType<
    typeof ASSISTANT_AI_RESPONSE_FEEDBACK_COMMENT_DIALOG_ITEM_FRAGMENT
  >;
  feedback:
    | DocumentType<
        typeof ASSISTANT_AI_RESPONSE_FEEDBACK_COMMENT_DIALOG_FEEDBACK_FRAGMENT
      >
    | null
    | undefined;
  open: boolean;
  onClose: () => void;
}

export const AssistantAiResponseFeedbackCommentDialog = ({
  item,
  feedback,
  open,
  onClose,
}: Props) => {
  const [giveFeedbackOnConversationItem, { loading }] = useMutation(
    GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION,
    {
      completionMessage: 'Thank you for your feedback!',
      onCompleted: onClose,
    },
  );

  const { control, handleSubmit } =
    useForm<AssistantAiResponseFeedbackFormData>({
      values: {
        conversationItem: item._id ?? '',
        rating: feedback?.rating ?? 'UNRATED',
        comment: feedback?.comment ?? '',
      },
    });

  const onSubmit = async (params: AssistantAiResponseFeedbackFormData) => {
    giveFeedbackOnConversationItem({
      variables: {
        params,
      },
    });
  };

  return (
    <Or2Dialog
      title="Help us improve AI Assistant"
      actions={
        <>
          <Or2Button
            disabled={loading}
            color="secondary"
            size="small"
            onClick={onClose}
          >
            Close
          </Or2Button>
          <Or2Button
            disabled={loading}
            size="small"
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Or2Button>
        </>
      }
      open={open}
      onClose={onClose}
    >
      <AssistantAiResponseFeedbackForm control={control} />
    </Or2Dialog>
  );
};
